import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { alignHorizontalScrollContainers } from '../rtlScroll';

const LanguageContext = createContext(null);

function translatePage(language) {
  const isArabic = language === 'ar';

  document.documentElement.lang = language;
  document.documentElement.dir = isArabic ? 'rtl' : 'ltr';

  document.querySelectorAll('[data-en]').forEach((element) => {
    const text = element.getAttribute(isArabic ? 'data-ar' : 'data-en');
    if (!text) return;

    if (element.matches('input, textarea')) return;

    const childTextTarget = element.matches('button, a, .size-option, .side-option, .radio-option')
      ? element.querySelector('span')
      : null;

    if (childTextTarget) {
      childTextTarget.textContent = text;
    } else if (element.children.length === 0 || element.matches('option, h1, h2, h3, h4, p, label, li')) {
      element.textContent = text;
    }
  });

  document.querySelectorAll('[data-placeholder-en]').forEach((element) => {
    const placeholder = element.getAttribute(isArabic ? 'data-placeholder-ar' : 'data-placeholder-en');
    if (placeholder) element.setAttribute('placeholder', placeholder);
  });

  window.dispatchEvent(new CustomEvent('languagechange', { detail: { language } }));
}

export function LanguageProvider({ children }) {
  const location = useLocation();
  const [language, setLanguageState] = useState(() => localStorage.getItem('language') === 'ar' ? 'ar' : 'en');

  useEffect(() => {
    localStorage.setItem('language', language);
    translatePage(language);

    let animationFrame;
    const alignScrollRows = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        alignHorizontalScrollContainers(language);
      });
    };
    const observer = new MutationObserver(alignScrollRows);

    alignScrollRows();
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', alignScrollRows);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      observer.disconnect();
      window.removeEventListener('resize', alignScrollRows);
    };
  }, [language, location.pathname]);

  const value = useMemo(() => ({
    language,
    setLanguage: setLanguageState,
    t: (english, arabic) => language === 'ar' ? arabic : english,
  }), [language]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used inside LanguageProvider');
  return context;
}
